import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { DevAuthService } from 'src/app/core/auth/dev-auth.service';

/** Error code of the Portal-API when the token's session was ended (security page, "sign out everywhere"). */
export const API_ERROR_SESSION_REVOKED = 'session_revoked';

/** True for a Portal-API error body `{ error: 'session_revoked' }`. */
export function isSessionRevokedBody(body: unknown): boolean {
  return (body as { error?: string } | null)?.error === API_ERROR_SESSION_REVOKED;
}

/**
 * The API answers 401 `session_revoked` once the session was ended elsewhere. The token itself is still
 * valid, so a re-login redirect would silently sign the user straight back in: tell them, then log out.
 * Shown once, however many requests fail at the same time.
 */
@Injectable({ providedIn: 'root' })
export class SessionRevokedService {
  private shown = false;

  constructor(
    private readonly alertCtrl: AlertController,
    private readonly translate: TranslateService,
    private readonly auth: DevAuthService,
  ) {}

  async handle(): Promise<void> {
    if (this.shown) return;
    this.shown = true;

    const alert = await this.alertCtrl.create({
      header: this.translate.instant('session.revoked.title'),
      message: this.translate.instant('session.revoked.message'),
      backdropDismiss: false,
      buttons: [{ text: this.translate.instant('session.revoked.confirm'), role: 'confirm' }],
    });
    await alert.present();
    await alert.onDidDismiss();
    this.auth.logout();
  }
}
